import React from "react";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Divider,
  Grid2,
  Typography,
  useTheme,
} from "@mui/material";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, Controller } from "react-hook-form";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import {
  AccountHolder,
  CACHE_KEYS,
  Region,
  useUpdateNaturalPerson,
  useUpdateUserProfile,
} from "@rentablo/api/apiv1";
import { RegionSelector } from "@components";
import { RHFTextField } from "@rentablo/components";
import { useMyself } from "@api/v1/hooks/useMyself";

const Form = styled.form`
  display: flex;
  padding: ${({ theme }) => theme.spacing(4, 0)};
  flex-direction: column;
  max-width: 700px;
`;

const StyledFieldContainer = styled(Grid2)(({ theme }) => ({
  "&:not(:last-child)": {
    marginBottom: theme.spacing(6),
  },
  padding: 0,
}));

const FormActions = styled.div`
  display: flex;
  width: 100%;

  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  padding-top: ${({ theme }) => theme.spacing(6)};
  ${({ theme }) => theme.breakpoints.down("sm")} {
    padding: ${({ theme }) => theme.spacing(2, 0)};
    flex-direction: column;
    align-items: unset;
  }
`;

interface FormFields {
  street: string;
  streetNumber: string;
  zip: string;
  city: string;
  region: Region;
  name: string;
  phone: string;
}

export interface AccountDataFormProps {
  accountHolder: AccountHolder;
}

export const AccountDataForm = (props: AccountDataFormProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const { accountHolder } = props;

  const [naturalPerson] = (accountHolder.naturalPersons || []).filter(
    (np) => np.isPrimaryAccountHolder
  );

  const { userReference: user } = accountHolder;
  const navigate = useNavigate();
  const { data: myself } = useMyself();

  const schema = Yup.object().shape({
    street: Yup.string().required(t("form.errors.required")),
    streetNumber: Yup.string().required(t("form.errors.required")),
    zip: Yup.string().required(t("form.errors.required")),
    city: Yup.string().required(t("form.errors.required")),
    region: Yup.object({
      id: Yup.number(),
      twoLetterCode: Yup.string(),
      threeLetterCode: Yup.string(),
      isoCode: Yup.string(),
      name: Yup.string(),
      germanName: Yup.string(),
      hasCapital: Yup.boolean(),
      capital: Yup.string(),
      regionType: Yup.string(),
      nationalClientIdentifier: Yup.string(),
      parentId: Yup.number(),
    }),
    name: Yup.string().required(t("form.errors.required")),
    phone: Yup.string().required(t("form.errors.required")),
  });

  const { handleSubmit, control, setValue } = useForm<FormFields>({
    resolver: yupResolver(schema),
    defaultValues: {
      region: naturalPerson.address?.region,
      phone: user.phone,
      city: naturalPerson.address?.city,
      zip: naturalPerson.address?.zip,
      streetNumber: naturalPerson.address?.streetNumber,
      street: naturalPerson.address?.street,
      name: `${naturalPerson.firstName} ${naturalPerson.lastName}`,
    },
  });

  const queryClient = useQueryClient();

  const { mutate } = useUpdateNaturalPerson({
    onSuccess: () => {
      queryClient
        .invalidateQueries({
          queryKey: [CACHE_KEYS.ACCOUNT_HOLDERS_ID, { id: accountHolder.id }],
        })
        .then(() => navigate("/cashback?tab=contacts"));
    },
  });
  const { mutate: mutateProfile } = useUpdateUserProfile({
    throwOnError: true,
  });

  const onSubmit = async (form: FormFields) => {
    await mutateProfile({
      id: myself!.profileReference.id,
      createOrUpdateProfileParams: { phoneNumber: form.phone },
    });
    await mutate({
      id: naturalPerson.id,
      createOrUpdateNaturalPersonParams: {
        createOrUpdateAddressParams: {
          street: form.street,
          zip: form.zip,
          city: form.city,
          streetNumber: form.streetNumber,
          regionId: form.region.id,
        },
      },
      onlyUpdateFieldsFor: ["address"],
    });
  };

  return (
    <Form
      onSubmit={handleSubmit(onSubmit)}
      data-testid="cashback-dashboard-edit-contact"
    >
      <Grid2 container>
        <StyledFieldContainer size={{ xs: 12 }}>
          <RHFTextField
            label={t("domains.cashback.accountDataForm.name")}
            name="name"
            disabled
            control={control}
            fullWidth
          />
          <Box pt={1}>
            <Typography variant="caption">
              {t("domains.cashback.accountDataForm.nameWarning")}
            </Typography>
          </Box>
        </StyledFieldContainer>
        <StyledFieldContainer size={{ xs: 12 }}>
          <RHFTextField
            label={t("domains.cashback.accountDataForm.phone")}
            name="phone"
            control={control}
            fullWidth
          />
        </StyledFieldContainer>
      </Grid2>
      <Divider
        sx={{ paddingTop: theme.spacing(5), marginBottom: theme.spacing(4) }}
      />
      <Grid2 container columnSpacing={3}>
        <Grid2 mb={3} size={12}>
          <Typography variant="body4" color="textSecondary">
            {t("domains.cashback.accountDataForm.anschrift")}
          </Typography>
        </Grid2>
        <StyledFieldContainer
          size={{
            xs: 12,
            md: 10,
          }}
        >
          <RHFTextField
            label={t("domains.cashback.accountDataForm.street")}
            name="street"
            control={control}
            fullWidth
          />
        </StyledFieldContainer>
        <StyledFieldContainer
          size={{
            xs: 12,
            md: 2,
          }}
        >
          <RHFTextField
            label={t("domains.cashback.accountDataForm.number")}
            name="streetNumber"
            control={control}
            fullWidth
          />
        </StyledFieldContainer>
        <StyledFieldContainer
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <RHFTextField
            label={t("domains.cashback.accountDataForm.zip")}
            name="zip"
            control={control}
            fullWidth
          />
        </StyledFieldContainer>
        <StyledFieldContainer
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <RHFTextField
            label={t("domains.cashback.accountDataForm.city")}
            name="city"
            control={control}
            fullWidth
          />
        </StyledFieldContainer>
        <StyledFieldContainer
          size={{
            xs: 12,
          }}
        >
          <Controller
            name="region"
            control={control}
            render={({ field }) => (
              <RegionSelector
                {...field}
                variant="outlined"
                defaultRegion={naturalPerson.address?.region}
                onChange={(value) => setValue("region", value!)}
              />
            )}
          />
        </StyledFieldContainer>
      </Grid2>
      <FormActions>
        <Button type="submit" variant="primary">
          {t("domains.cashback.form.save")}
        </Button>
        <Button
          variant="tertiary"
          onClick={() => navigate("/cashback?tab=contacts")}
        >
          {t("domains.cashback.form.cancel")}
        </Button>
      </FormActions>
    </Form>
  );
};
